import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthService }        from '../../services/auth.service';
import { FeedbackUiService }  from '../../services/feedback-ui.service';
import { LogBugUiService }    from '../../services/log-bug-ui.service';
import { User }               from '../../models/user.model';

@Component({
  selector: 'sp-user-menu',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
})
export class UserMenuComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  user: User | null = null;
  isDropdownOpen  = false;
  isProfileOpen   = false;

  constructor(
    private auth:        AuthService,
    private feedbackUi:  FeedbackUiService,
    private logBugUi:    LogBugUiService,
    private cdr:         ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.auth.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(u => { this.user = u; this.cdr.markForCheck(); });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get initials(): string {
    return this.user?.name.charAt(0).toUpperCase() ?? '?';
  }

  // ── Dropdown ────────────────────────────────────────────────────────────────

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    this.cdr.markForCheck();
  }

  @HostListener('document:click')
  closeDropdown(): void {
    if (this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  // ── Profile modal ────────────────────────────────────────────────────────────

  openProfile(): void {
    this.isDropdownOpen = false;
    this.isProfileOpen  = true;
    this.cdr.markForCheck();
  }

  closeProfile(): void {
    this.isProfileOpen  = false;
    this.cdr.markForCheck();
  }

  // ── Feedback (rating survey) ─────────────────────────────────────────────────

  openFeedback(): void {
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
    this.feedbackUi.open();
  }

  // ── Log Bug (detailed defect report) ─────────────────────────────────────────

  openLogBug(): void {
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
    this.logBugUi.open();
  }

  // ── Auth ─────────────────────────────────────────────────────────────────────

  signOut(): void {
    this.isDropdownOpen = false;
    this.auth.logout();
  }
}
